/**
 * Web implementation of useFocusRing.
 *
 * Determines whether a focus ring should be shown to indicate keyboard focus.
 * Focus rings are visible only when the user is interacting with a keyboard,
 * not with a mouse, touch, or other pointer input.
 *
 * Inspired by:
 * - https://github.com/gluestack/gluestack-ui/blob/main/packages/gluestack-utils/src/aria/focus/useFocusRing.web.ts
 * - https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/focus/src/useFocusRing.ts
 * - https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/interactions/src/useFocusVisible.ts
 */
import { useCallback, useEffect, useRef, useState } from "react";

type Modality = "keyboard" | "pointer" | "virtual";

let currentModality: Modality | null = null;
const changeHandlers = new Set<() => void>();
let isSetupComplete = false;

function triggerChangeHandlers() {
  changeHandlers.forEach((handler) => handler());
}

function handleKeyboardEvent(e: KeyboardEvent) {
  // Ignore modifier-only keypresses that don't indicate intentional navigation
  if (e.metaKey || e.ctrlKey || e.key === "Control" || e.key === "Shift" || e.key === "Meta") {
    return;
  }
  currentModality = "keyboard";
  triggerChangeHandlers();
}

function handlePointerEvent() {
  currentModality = "pointer";
  triggerChangeHandlers();
}

function setupGlobalFocusListeners() {
  if (isSetupComplete || typeof document === "undefined") {
    return;
  }
  isSetupComplete = true;

  // keydown fires before focus moves, which is what we need to detect keyboard navigation.
  // keyup is not needed — modality is determined at the start of the interaction.
  document.addEventListener("keydown", handleKeyboardEvent, true);

  // Use PointerEvent when available (subsumes mouse and touch).
  // Fall back to mousedown/touchstart only in environments without PointerEvent support.
  if (typeof PointerEvent !== "undefined") {
    document.addEventListener("pointerdown", handlePointerEvent, true);
  } else {
    document.addEventListener("mousedown", handlePointerEvent, true);
    document.addEventListener("touchstart", handlePointerEvent, true);
  }
}

function isFocusVisible(): boolean {
  // Focus is visible when the last interaction was keyboard or virtual (e.g. screen reader),
  // but not when it was a pointer (mouse/touch).
  return currentModality !== "pointer";
}

export interface FocusRingProps {
  /** Whether the element will be auto focused. */
  autoFocus?: boolean;
}

export interface FocusRingAria {
  /** Whether the element is currently focused. */
  isFocused: boolean;
  /** Whether keyboard focus should be visible. */
  isFocusVisible: boolean;
  /**
   * Props to spread onto the focusable element.
   * Handlers intentionally omit the event argument for React Native compatibility
   * (RN Pressable onFocus/onBlur use a different event type than React DOM).
   */
  focusProps: {
    onFocus?: () => void;
    onBlur?: () => void;
  };
}

/**
 * Determines whether a focus ring should be shown to indicate keyboard focus.
 * Focus rings are visible only when the user is interacting with a keyboard,
 * not with a mouse, touch, or other input methods.
 */
export function useFocusRing(props: FocusRingProps = {}): FocusRingAria {
  const { autoFocus = false } = props;

  setupGlobalFocusListeners();

  const [isFocused, setFocused] = useState(false);
  const [isFocusVisibleState, setFocusVisible] = useState(() => autoFocus && isFocusVisible());

  const stateRef = useRef({ isFocused: false });

  const updateFocusVisible = useCallback(() => {
    if (stateRef.current.isFocused) {
      setFocusVisible(isFocusVisible());
    }
  }, []);

  useEffect(() => {
    changeHandlers.add(updateFocusVisible);
    return () => {
      changeHandlers.delete(updateFocusVisible);
    };
  }, [updateFocusVisible]);

  const onFocus = useCallback(() => {
    stateRef.current.isFocused = true;
    setFocused(true);
    setFocusVisible(isFocusVisible());
  }, []);

  const onBlur = useCallback(() => {
    stateRef.current.isFocused = false;
    setFocused(false);
    setFocusVisible(false);
  }, []);

  return {
    isFocused,
    isFocusVisible: isFocusVisibleState,
    focusProps: { onFocus, onBlur },
  };
}
